import { useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import FloatingButton from "./FloatingButton";
import SEOHead from "./SEOHead";
import Avis from "./Avis";
import ImagePlaceholder from "@/components/base/ImagePlaceholder";
import ContactForm from "@/components/base/ContactForm";
import ContactInfo from "@/components/base/ContactInfo";
import DiagnosticVideo from "@/components/feature/DiagnosticVideo";
import SectionLocale from "./SectionLocale";
import Breadcrumb from "./Breadcrumb";

interface CityPageTemplateProps {
  city: string;
  para1: string;
  para2: string;
  imageUrl: string;
  imageAlt: string;
}

const services = [
  {
    title: "Réparation",
    desc: "Volet bloqué, moteur, sangle, cordon, tablier : nous réparons tous les types.",
    href: "/reparation-volet-roulant",
    icon: "ri-tools-line",
  },
  {
    title: "Dépannage urgence",
    desc: "Intervention rapide en cas de volet bloqué ou coincé, toutes marques.",
    href: "/depannage-urgence",
    icon: "ri-alarm-warning-line",
  },
  {
    title: "Remplacement de moteur",
    desc: "Moteur Somfy, Nice et autres marques. Installation et reprogrammation.",
    href: "/moteur-somfy",
    icon: "ri-flashlight-line",
  },
  {
    title: "Sangle",
    desc: "Remplacement de sangle et cordon usés, toutes dimensions et toutes marques.",
    href: "/sangle-volet-roulant",
    icon: "ri-links-line",
  },
  {
    title: "Tablier",
    desc: "Remplacement de lames ou de tablier complet en PVC et aluminium.",
    href: "/tablier-volet-roulant",
    icon: "ri-layout-masonry-line",
  },
  {
    title: "Motorisation",
    desc: "Transformation manuel vers électrique. Motorisation et programmation.",
    href: "/motorisation-somfy",
    icon: "ri-settings-4-line",
  },
  {
    title: "Pose",
    desc: "Pose et installation de volets roulants neufs, sur mesure et sur-mesure.",
    href: "/pose-volet-roulant",
    icon: "ri-add-box-line",
  },
];

const maillageLinks = [
  {
    title: "Accueil",
    desc: "Retournez à la page d'accueil pour découvrir tous nos services de réparation et motorisation.",
    href: "/",
    icon: "ri-home-line",
  },
  {
    title: "Zones d'intervention",
    desc: "Nous intervenons dans tout le Bas-Rhin : Strasbourg et les autres communes.",
    href: "/zones-d-intervention",
    icon: "ri-map-pin-line",
  },
  {
    title: "Dépannage urgence",
    desc: "Volet bloqué en urgence ? Intervention rapide à Strasbourg et dans le Bas-Rhin.",
    href: "/depannage-urgence",
    icon: "ri-alarm-warning-line",
  },
];

const reassurances = [
  {
    icon: "ri-time-line",
    label: "Intervention 24h",
    desc: "Urgence et dépannage",
  },
  {
    icon: "ri-file-list-3-line",
    label: "Devis gratuit",
    desc: "Sans engagement",
  },
  {
    icon: "ri-shield-check-line",
    label: "Toutes marques",
    desc: "Somfy, Nice et autres",
  },
  {
    icon: "ri-award-line",
    label: "Garantie 10 ans",
    desc: "Pièces et main d'œuvre",
  },
];


export default function CityPageTemplate({
  city,
  para1,
  para2,
  imageUrl,
  imageAlt,
}: CityPageTemplateProps) {
  const [faqOpen, setFaqOpen] = useState<number | null>(0);
  const location = useLocation();
  const path = location.pathname;

  const title = `Réparation Volet Roulant ${city} | Dépannage 24h Dépan'Volets`;
  const description = `Réparation, motorisation et pose de volet roulant à ${city} (Bas-Rhin). Toutes marques, intervention rapide, devis gratuit. 03 74 47 32 40.`;

  const faqs = [
    {
      question: `Intervenez-vous vite à ${city} ?`,
      answer: `Oui, nos techniciens se déplacent régulièrement à ${city} et dans le Bas-Rhin. Pour les urgences, nous intervenons dans les meilleurs délais. Pour les interventions programmées, nous fixons un rendez-vous adapté à votre disponibilité.`,
    },
    {
      question: `Le déplacement est-il payant à ${city} ?`,
      answer: `Non, le déplacement et le diagnostic sont gratuits à ${city} comme dans tout le Bas-Rhin. Vous ne payez que les réparations réalisées, après acceptation du devis. Aucun frais caché.`,
    },
    {
      question: `Réparez-vous toutes les marques à ${city} ?`,
      answer: `Oui, nous réparons toutes les marques de volets roulants à ${city} : Somfy, Nice, Bubendorff, Profalux, Simu, Becker et bien d'autres. Qu'il s'agisse d'un volet manuel ou motorisé, nous disposons des pièces et du savoir-faire nécessaires.`,
    },
  ];

  const realisations = [
    {
      src: "https://storage.readdy-site.link/project_files/f33f6fb7-f97a-4019-914a-f9e90e42cb50/588cf9d6-2f92-4d17-9eb0-b7a81cac8b9d_2020-02-05.jpg?v=29a04691139e6bff9e6c7ee3f25382bc",
      alt: `Réparation de volet roulant à ${city}`,
    },
    {
      src: "https://storage.readdy-site.link/project_files/f33f6fb7-f97a-4019-914a-f9e90e42cb50/c4980f39-8939-4f0f-be91-f402a5b4bc02_Beratung-zur-Rollladen-Modernisierung-am-Haus.webp?v=afd6feffb93d7cac203938a0df6fc167",
      alt: `Pose de volet roulant à ${city}`,
    },
    {
      src: "https://static.readdy.ai/image/60b7a1bdc124301cce40c5bb19c672c9/feb84a2a99fbcd7e8ff78c3e17f09320.jpeg",
      alt: `Motorisation de volet roulant à ${city}`,
    },
  ];

  return (
    <div className="font-sans">
      <SEOHead
        title={title}
        description={description}
        path={path}
        city={city}
        serviceType="Réparation de volet roulant"
        faqs={faqs}
        type="service"
        breadcrumbs={[
          { label: "Accueil", href: "/" },
          { label: "Zones d'intervention", href: "/zones-d-intervention" },
          { label: city },
        ]}
      />
      <Navbar />
      <Breadcrumb
        items={[
          { label: "Accueil", href: "/" },
          { label: "Zones d'intervention", href: "/zones-d-intervention" },
          { label: city },
        ]}
      />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[600px] h-auto overflow-hidden">
          <div className="absolute inset-0">
            <ImagePlaceholder
              src={imageUrl}
              alt={imageAlt}
              className="absolute inset-0 w-full h-full"
              loading="eager"
            />
          </div>
          <div className="absolute inset-0 bg-[#143A66]/70"></div>
          <div className="relative z-10 flex flex-col items-center justify-center min-h-[420px] md:min-h-[600px] px-4 text-center">
            <div className="max-w-[80%] mx-auto">
              <h1 className="text-2xl md:text-5xl font-bold text-white mb-3 md:mb-4 animate-hero-fade-up">
                Réparation et motorisation de volet roulant à {city}
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-5 md:mb-8 animate-hero-fade-up animate-delay-100">
                Nos techniciens interviennent à {city} et alentours, toutes marques, partout dans le Bas-Rhin.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-2 md:gap-4 animate-hero-fade-up animate-delay-200 justify-center">
                <a
                  href="tel:0374473240"
                  className="bg-brand-cyan text-white px-5 py-3 md:px-8 md:py-3.5 rounded-full font-semibold text-base hover:bg-brand-cyan/90 transition-all duration-200 btn-hover-effect whitespace-nowrap"
                >
                  Appeler le 03 74 47 32 40
                </a>
                <a
                  href="#devis"
                  className="border border-white text-white px-5 py-3 md:px-8 md:py-3.5 rounded-full font-semibold text-base hover:bg-white/10 transition-all duration-200 btn-hover-effect whitespace-nowrap"
                >
                  Devis gratuit
                </a>
              </div>
            </div>
          </div>
        </section>

        <DiagnosticVideo />

        {/* Réassurances */}
        <section className="bg-white py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {reassurances.map((item) => (
                <div
                  key={item.label}
                  className="bg-white rounded-xl border border-brand-border p-5 md:p-6 flex flex-col items-center text-center"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-light mb-3">
                    <i className={`${item.icon} text-brand-cyan text-xl`}></i>
                  </div>
                  <p className="text-brand-blue text-base font-bold mb-1">{item.label}</p>
                  <p className="text-brand-text text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="bg-brand-light py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-brand-blue mb-3">
                Nos services à {city}
              </h2>
              <p className="text-brand-text text-base md:text-lg max-w-2xl mx-auto">
                Toutes nos prestations sont disponibles à {city} et dans les communes environnantes du Bas-Rhin.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {services.map((s) => (
                <a
                  key={s.title}
                  href={s.href}
                  className="group bg-white rounded-xl border border-brand-border p-5 md:p-6 hover:border-brand-cyan transition-all flex flex-col"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-light mb-4">
                    <i className={`${s.icon} text-brand-cyan text-lg`}></i>
                  </div>
                  <h3 className="text-brand-blue text-base font-bold mb-2 group-hover:text-brand-cyan transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-brand-text text-sm leading-relaxed">{s.desc}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Texte local */}
        <section className="bg-white py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-brand-blue">
                Votre réparateur de volet roulant à {city}
              </h2>
            </div>
            <div className="space-y-6 text-brand-text text-base leading-relaxed">
              <p>{para1}</p>
              <p>{para2}</p>
            </div>
          </div>
        </section>

        {/* Section locale — contenu 2 colonnes */}
        <SectionLocale />

        {/* CTA milieu */}
        <section className="bg-brand-blue py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Besoin d'une intervention à {city} ?
            </h2>
            <p className="text-white/80 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              Nos techniciens sont disponibles pour tous vos besoins de réparation, motorisation et pose de volet roulant dans le Bas-Rhin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:0374473240"
                className="bg-brand-cyan text-white px-8 py-3.5 rounded-full font-semibold text-base hover:bg-brand-cyan/90 transition-all duration-200 btn-hover-effect whitespace-nowrap"
              >
                <span className="btn-icon-wrap"><i className="ri-phone-line mr-2"></i></span>
                Appeler le 03 74 47 32 40
              </a>
              <a
                href="#devis"
                className="border-2 border-white text-white px-8 py-3.5 rounded-full font-semibold text-base hover:bg-white hover:text-brand-blue transition-all duration-200 btn-hover-effect whitespace-nowrap"
              >
                Devis gratuit
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-brand-light py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-brand-blue mb-3">
                Questions fréquentes — {city}
              </h2>
              <p className="text-brand-text text-base md:text-lg">
                Retrouvez les réponses aux questions les plus courantes.
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-brand-border overflow-hidden"
                >
                  <button
                    onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                  >
                    <span className="text-brand-blue font-semibold text-base pr-4">
                      {faq.question}
                    </span>
                    <div className="w-8 h-8 flex items-center justify-center shrink-0">
                      <i
                        className={`ri-arrow-down-s-line text-brand-cyan text-xl transition-transform ${
                          faqOpen === i ? "rotate-180" : ""
                        }`}
                      ></i>
                    </div>
                  </button>
                  {faqOpen === i && (
                    <div className="px-5 md:px-6 pb-5 md:pb-6">
                      <p className="text-brand-text text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Galerie */}
        <section className="bg-white py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-brand-blue mb-3">
                Nos réalisations à {city}
              </h2>
              <p className="text-brand-text text-base md:text-lg max-w-2xl mx-auto">
                Découvrez quelques-unes de nos interventions de réparation et de pose de volet roulant à {city}.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {realisations.map((item, i) => (
                <div
                  key={i}
                  className="bg-brand-light rounded-xl border border-brand-border aspect-[4/3] overflow-hidden"
                >
                  <ImagePlaceholder
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Formulaire */}
        <section id="devis" className="bg-brand-light py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 lg:gap-10 min-w-0">
              <div className="lg:col-span-3">
                <h2 className="text-2xl md:text-4xl font-bold text-brand-blue mb-3">
                  Demandez un devis gratuit
                </h2>
                <p className="text-brand-text text-base md:text-lg mb-8">
                  Remplissez le formulaire ci-dessous, nous vous recontactons sous 24h.
                </p>
                <ContactForm
                  page={city}
                  subject={`Nouveau lead Dépan'Volets — ${city}`}
                  variant="wide"
                  showTitle={false}
                />
              </div>
              <div className="lg:col-span-2">
                <ContactInfo variant="light" />
              </div>
            </div>
          </div>
        </section>

        {/* Maillage */}
        <section className="bg-white py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-brand-blue mb-3">
                Vous cherchez une autre prestation ?
              </h2>
              <p className="text-brand-text text-base md:text-lg max-w-2xl mx-auto">
                Explorez nos autres pages ou revenez à l'accueil pour découvrir l'ensemble de nos services.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {maillageLinks.map((link) => (
                <a
                  key={link.title}
                  href={link.href}
                  className="group bg-brand-light rounded-xl border border-brand-border p-5 md:p-6 hover:border-brand-cyan transition-all flex flex-col"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-light border border-brand-border mb-4">
                    <i className={`${link.icon} text-brand-cyan text-lg`}></i>
                  </div>
                  <h3 className="text-brand-blue text-base font-bold mb-2 group-hover:text-brand-cyan transition-colors">
                    {link.title}
                  </h3>
                  <p className="text-brand-text text-sm leading-relaxed">{link.desc}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Avis />
      <Footer />
      <FloatingButton />
    </div>
  );
}